import { getAuth } from 'firebase/auth';

import { scoringApp } from '../firebase';

const GET_UPLOAD_URL_FUNCTION =
  'https://us-central1-cheereval.cloudfunctions.net/getStreamUploadUrl';
const GET_PLAYBACK_TOKEN_FUNCTION =
  'https://us-central1-cheereval.cloudfunctions.net/getStreamPlaybackToken';
const GET_VIDEO_STATUS_FUNCTION =
  'https://us-central1-cheereval.cloudfunctions.net/getStreamVideoStatus';
const DELETE_VIDEO_FUNCTION =
  'https://us-central1-cheereval.cloudfunctions.net/deleteStreamVideo';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GetStreamUploadUrlResponse {
  uploadUrl: string;
  uid: string;
}

interface GetStreamPlaybackTokenResponse {
  iframeUrl: string;
}

interface GetStreamVideoStatusResponse {
  readyToStream: boolean;
  state: string;
  width?: number;
  height?: number;
}

interface ErrorResponse {
  error: string;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class StreamServiceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StreamServiceError';
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function getIdToken(): Promise<string> {
  const user = getAuth(scoringApp).currentUser;
  if (!user) {
    throw new StreamServiceError('You must be signed in to use video features.');
  }
  return user.getIdToken();
}

async function extractErrorMessage(response: globalThis.Response): Promise<string> {
  try {
    const body = (await response.json()) as ErrorResponse;
    if (typeof body.error === 'string' && body.error.length > 0) return body.error;
  } catch { /* ignore */ }
  return `Request failed (${response.status}).`;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

/**
 * Requests a one-time Cloudflare Stream upload URL from the backend.
 * The blob should be uploaded to the returned uploadUrl immediately.
 */
export async function getStreamUploadUrl(): Promise<GetStreamUploadUrlResponse> {
  const idToken = await getIdToken();
  let response: globalThis.Response;
  try {
    response = await fetch(GET_UPLOAD_URL_FUNCTION, {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}` },
    });
  } catch (err) {
    console.error('[getStreamUploadUrl] fetch failed:', err);
    throw new StreamServiceError('Could not reach the server. Please check your connection.');
  }
  if (!response.ok) {
    const message = await extractErrorMessage(response);
    console.error('[getStreamUploadUrl] server error', response.status, message);
    throw new StreamServiceError(message);
  }
  return (await response.json()) as GetStreamUploadUrlResponse;
}

/**
 * POSTs a video blob directly to a Cloudflare Stream one-time upload URL.
 * The uploadUrl must come from getStreamUploadUrl() and be used only once.
 */
export class StreamUploadAbortedError extends Error {
  constructor() {
    super('Upload aborted');
    this.name = 'StreamUploadAbortedError';
  }
}

export async function uploadVideoToStream(
  uploadUrl: string,
  blob: Blob,
  mimeType: string,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
  const formData = new FormData();
  formData.append('file', blob, `video.${ext}`);
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', uploadUrl);
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        console.error('[uploadVideoToStream] Cloudflare upload error', xhr.status);
        reject(new StreamServiceError('Video upload failed. Please try again.'));
      }
    };
    xhr.onerror = () => {
      console.error('[uploadVideoToStream] XHR network error');
      reject(new StreamServiceError('Upload failed. Please check your connection and try again.'));
    };
    xhr.onabort = () => reject(new StreamUploadAbortedError());
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(formData);
  });
}

/**
 * Requests a signed Cloudflare Stream iframe playback URL for a private video.
 * The token is valid for 1 hour.
 */
export async function getStreamPlaybackToken(
  videoUid: string,
): Promise<GetStreamPlaybackTokenResponse> {
  const idToken = await getIdToken();
  let response: globalThis.Response;
  try {
    response = await fetch(GET_PLAYBACK_TOKEN_FUNCTION, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ videoUid }),
    });
  } catch (err) {
    console.error('[getStreamPlaybackToken] fetch failed:', err);
    throw new StreamServiceError('Could not reach the server. Please check your connection.');
  }
  if (!response.ok) {
    const message = await extractErrorMessage(response);
    console.error('[getStreamPlaybackToken] server error', response.status, message);
    throw new StreamServiceError(message);
  }
  return (await response.json()) as GetStreamPlaybackTokenResponse;
}

/**
 * Checks the processing status of a Cloudflare Stream video.
 * Poll until readyToStream is true before showing the player.
 */
export async function getStreamVideoStatus(
  videoUid: string,
): Promise<GetStreamVideoStatusResponse> {
  const idToken = await getIdToken();
  let response: globalThis.Response;
  try {
    response = await fetch(GET_VIDEO_STATUS_FUNCTION, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ videoUid }),
    });
  } catch (err) {
    console.error('[getStreamVideoStatus] fetch failed:', err);
    throw new StreamServiceError('Could not reach the server. Please check your connection.');
  }
  if (!response.ok) {
    const message = await extractErrorMessage(response);
    throw new StreamServiceError(message);
  }
  return (await response.json()) as GetStreamVideoStatusResponse;
}

/**
 * Deletes a Cloudflare Stream video from the backend.
 */
export async function deleteStreamVideo(videoUid: string): Promise<void> {
  const idToken = await getIdToken();
  let response: globalThis.Response;
  try {
    response = await fetch(DELETE_VIDEO_FUNCTION, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ videoUid }),
    });
  } catch (err) {
    console.error('[deleteStreamVideo] fetch failed:', err);
    throw new StreamServiceError('Could not reach the server. Please check your connection.');
  }
  if (!response.ok) {
    const message = await extractErrorMessage(response);
    console.error('[deleteStreamVideo] server error', response.status, message);
    throw new StreamServiceError(message);
  }
}
